class SwagTeamsMenu extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabs = Array.from(this.querySelectorAll('[data-teams-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-teams-panel]'));
    this.search = this.querySelector('[data-teams-search]');
    this.emptyState = this.querySelector('[data-teams-empty]');
    this.recent = this.querySelector('[data-teams-recent]');
    this.recentList = this.querySelector('[data-teams-recent-list]');
    this.storageKey = this.dataset.storageKey || 'swagTeamsRecentlyViewedV1';
    this.teamLinks = Array.from(this.querySelectorAll('[data-team-link]'));
    this.teamMap = new Map();
    this.menuDetails = this.closest('[data-swag-teams-details]');

    this.teamLinks.forEach((link) => {
      const handle = link.dataset.teamHandle;
      if (handle && !this.teamMap.has(handle)) this.teamMap.set(handle, link);
    });

    this.onTabClick = this.handleTabClick.bind(this);
    this.onTabKeydown = this.handleTabKeydown.bind(this);
    this.onSearch = this.filterActiveLeague.bind(this);
    this.onTeamClick = this.handleTeamClick.bind(this);
    this.onRecentUpdate = this.renderRecent.bind(this);
    this.onMenuEnter = this.openHoverMenu.bind(this);
    this.onMenuFocus = this.openHoverMenu.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });
    this.search?.addEventListener('input', this.onSearch);
    this.addEventListener('click', this.onTeamClick);
    this.menuDetails?.addEventListener('mouseenter', this.onMenuEnter);
    this.menuDetails?.addEventListener('focusin', this.onMenuFocus);
    window.addEventListener('swag:teams-recent-updated', this.onRecentUpdate);
    window.addEventListener('storage', this.onRecentUpdate);

    this.classList.add('is-enhanced');
    if (this.tabs.length) this.activateTab(0);
    this.renderRecent();
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    this.search?.removeEventListener('input', this.onSearch);
    this.removeEventListener('click', this.onTeamClick);
    this.menuDetails?.removeEventListener('mouseenter', this.onMenuEnter);
    this.menuDetails?.removeEventListener('focusin', this.onMenuFocus);
    window.removeEventListener('swag:teams-recent-updated', this.onRecentUpdate);
    window.removeEventListener('storage', this.onRecentUpdate);
    this.initialized = false;
  }

  openHoverMenu() {
    if (this.menuDetails?.getAttribute('trigger') === 'hover') {
      this.menuDetails.open = true;
    }
  }

  handleTabClick(event) {
    this.activateTab(this.tabs.indexOf(event.currentTarget));
  }

  handleTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    this.activateTab(nextIndex, true);
  }

  activateTab(index, moveFocus = false) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (moveFocus && active) tab.focus();
    });
    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });

    this.filterActiveLeague();
  }

  filterActiveLeague() {
    const query = (this.search?.value || '').trim().toLocaleLowerCase();
    const panel = this.panels.find((candidate) => !candidate.hidden);
    let visibleCount = 0;

    panel?.querySelectorAll('[data-team-item]').forEach((item) => {
      const name = item.querySelector('[data-team-link]')?.dataset.teamName || '';
      const visible = !query || name.toLocaleLowerCase().includes(query);
      item.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (this.emptyState) {
      this.emptyState.hidden = !panel || visibleCount > 0 || !query;
    }
  }

  handleTeamClick(event) {
    const link = event.target.closest('[data-team-link]');
    if (!link || !this.contains(link)) return;
    this.storeRecent(link.dataset.teamHandle);
  }

  readRecent() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored
        .filter((handle, index) => typeof handle === 'string' && this.teamMap.has(handle) && stored.indexOf(handle) === index)
        .slice(0, 5);
    }
    catch (error) {
      return [];
    }
  }

  storeRecent(handle) {
    if (!handle || !this.teamMap.has(handle)) return;
    const handles = [handle, ...this.readRecent().filter((storedHandle) => storedHandle !== handle)].slice(0, 5);

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(handles));
      window.dispatchEvent(new CustomEvent('swag:teams-recent-updated'));
    }
    catch (error) {
      // The menu remains fully usable when storage is unavailable.
    }
  }

  renderRecent(event) {
    if (event?.type === 'storage' && event.key !== this.storageKey) return;
    if (!this.recent || !this.recentList) return;

    const handles = this.readRecent();
    this.recentList.replaceChildren();

    handles.forEach((handle) => {
      const source = this.teamMap.get(handle);
      if (!source) return;
      const item = document.createElement('li');
      const link = source.cloneNode(true);
      link.classList.add('swag-teams__recent-link');
      item.append(link);
      this.recentList.append(item);
    });

    this.recent.hidden = this.recentList.children.length === 0;
  }
}

if (!customElements.get('swag-teams-menu')) {
  customElements.define('swag-teams-menu', SwagTeamsMenu);
}
